import { useState, useEffect } from 'react';
import { API_URL } from '../config.js';
import {
  Users,
  Ticket,
  Copy,
  Check,
  X,
  RefreshCw,
  UserPlus,
  Shield,
  Clock,
  CheckCircle,
  XCircle,
  Ban,
  Factory,
  Plus,
  Trash2,
  ArrowLeft,
  Activity,
  Bell,
  Database,
  Gauge
} from 'lucide-react';
import MetricsView from './MetricsView.jsx';
import MysqlInstancesView from './MysqlInstancesView.jsx';
import TenantQuotasView from './TenantQuotasView.jsx';


export default function AdminDashboard({ onBack }) {
  const [activeTab, setActiveTab] = useState('invitations');
  const [invitations, setInvitations] = useState([]);
  const [users, setUsers] = useState([]);
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  const [newPlant, setNewPlant] = useState({ plantId: '', name: '', description: '' });

  const [newInvite, setNewInvite] = useState({
    plantId: '',
    role: 'operator',
    expiresInDays: 7,
    maxUses: 1,
    features: { panel: false, scada: false, reports: false, sharing: false, alarms: false, templates: false }
  });

  // Always fetch plants on mount (needed for invitation dropdown)
  useEffect(() => {
    fetchPlants();
  }, []);

  useEffect(() => {
    if (activeTab === 'invitations') {
      fetchInvitations();
    } else if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'plants') {
      fetchPlants();
    }
  }, [activeTab]);

  const fetchInvitations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/admin/invitations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setInvitations(data.invitations);
      }
    } catch (err) {
      console.error('Error fetching invitations:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setUsers(data.users);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlants = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/admin/plants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setPlants(data.plants);
        // Set default plant for invitation if not set
        if (!newInvite.plantId && data.plants.length > 0) {
          setNewInvite(prev => ({ ...prev, plantId: data.plants[0].plantId }));
        }
      }
    } catch (err) {
      console.error('Error fetching plants:', err);
    }
  };

  const createPlant = async () => {
    if (!newPlant.plantId || !newPlant.name) {
      alert('Plant ID and Name are required');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/admin/plants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newPlant)
      });
      const data = await res.json();
      if (data.success) {
        setNewPlant({ plantId: '', name: '', description: '' });
        fetchPlants();
        alert(`Plant "${data.plant.name}" created successfully!`);
      } else {
        alert(data.message || 'Failed to create plant');
      }
    } catch (err) {
      console.error('Error creating plant:', err);
    }
  };

  const deletePlant = async (plantId) => {
    if (!confirm(`Delete plant "${plantId}"? This does NOT remove user access or dashboards — only removes the plant from the registry.`)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/admin/plants/${plantId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        fetchPlants();
        alert(`Plant deleted. Affected: ${data.affected.users} users, ${data.affected.dashboards} dashboards, ${data.affected.activeInvitations} invitations.`);
      }
    } catch (err) {
      console.error('Error deleting plant:', err);
    }
  };

  const generateInvitation = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/admin/invitations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newInvite)
      });
      const data = await res.json();
      if (data.success) {
        fetchInvitations();
        // Copy share link to clipboard — this is the ONLY time it's available
        const textToCopy = `Code: ${data.code}\nShare Link: ${data.shareUrl}`;
        navigator.clipboard.writeText(textToCopy);
        alert(
          `Invitation created!\n\n` +
          `Code: ${data.code}\n` +
          `Share Link: ${data.shareUrl}\n\n` +
          `Both copied to clipboard.\n` +
          `Save it now — this code cannot be retrieved again.`
        );
      }
    } catch (err) {
      console.error('Error generating invitation:', err);
    }
  };

  const revokeInvitation = async (id) => {
    if (!confirm('Revoke this invitation code?')) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/admin/invitations/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        fetchInvitations();
      }
    } catch (err) {
      console.error('Error revoking invitation:', err);
    }
  };

  const updateFeatures = async (userId, features) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/admin/users/${userId}/features`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ features })
      });
      const data = await res.json();
      if (data.success) {
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, features: data.features } : u));
      } else {
        alert(data.message || 'Failed to update features');
      }
    } catch (err) {
      console.error('Error updating features:', err);
    }
  };

  const updateMaxSessions = async (userId, maxSessions) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/admin/users/${userId}/max-sessions`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ maxSessions })
      });
      const data = await res.json();
      if (data.success) {
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, maxSessions: data.maxSessions } : u));
      } else {
        alert(data.message || 'Failed to update session limit');
      }
    } catch (err) {
      console.error('Error updating maxSessions:', err);
    }
  };

  const approveUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/admin/users/${userId}/approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        fetchUsers();
        alert('User approved successfully!');
      }
    } catch (err) {
      console.error('Error approving user:', err);
    }
  };

  const suspendUser = async (userId) => {
    if (!confirm('Suspend this user?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/admin/users/${userId}/suspend`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        fetchUsers();
      }
    } catch (err) {
      console.error('Error suspending user:', err);
    }
  };

  const addPlantAccess = async (userId, plantId, role) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/admin/users/${userId}/plant-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ plantId, role })
      });
      const data = await res.json();
      if (data.success) {
        fetchUsers();
      } else {
        alert(data.message || 'Failed to add plant access');
      }
    } catch (err) {
      console.error('Error adding plant access:', err);
    }
  };

  const removePlantAccess = async (userId, plantId) => {
    if (!confirm(`Remove access to "${plantId}" for this user?`)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/admin/users/${userId}/plant-access/${plantId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        fetchUsers();
      } else {
        alert(data.message || 'Failed to remove plant access');
      }
    } catch (err) {
      console.error('Error removing plant access:', err);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(text);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {onBack && (
            <button
              onClick={onBack}
              style={{
                padding: '10px 16px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid #1e293b',
                borderRadius: 8,
                color: '#9ca3af',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 14,
                fontWeight: 600,
                transition: 'all 0.15s'
              }}
            >
              <ArrowLeft size={16} />
              Dashboard
            </button>
          )}
          <h1 style={styles.title}>Admin Dashboard</h1>
        </div>
        <div style={styles.tabs}>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'invitations' ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab('invitations')}
          >
            <Ticket size={18} />
            Invitations
          </button>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'users' ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab('users')}
          >
            <Users size={18} />
            Users
          </button>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'plants' ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab('plants')}
          >
            <Factory size={18} />
            Plants
          </button>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'metrics' ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab('metrics')}
          >
            <Activity size={18} />
            Metrics
          </button>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'mysql' ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab('mysql')}
          >
            <Database size={18} />
            MySQL
          </button>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'quotas' ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab('quotas')}
          >
            <Gauge size={18} />
            Quotas
          </button>
        </div>
      </div>

      {activeTab === 'metrics' && (
        <MetricsView />
      )}

      {activeTab === 'mysql' && (
        <MysqlInstancesView />
      )}

      {activeTab === 'quotas' && (
        <TenantQuotasView />
      )}

      {activeTab === 'invitations' && (
        <div style={styles.content}>
          {/* Generate New Invitation */}
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>
              <UserPlus size={20} />
              Generate New Invitation
            </h2>
            <div style={styles.form}>
              <div style={styles.formRow}>
                <label style={styles.label}>Plant</label>
                <select
                  style={styles.select}
                  value={newInvite.plantId}
                  onChange={(e) =>
                    setNewInvite({ ...newInvite, plantId: e.target.value })
                  }
                >
                  {plants.length === 0 && <option value="">No plants — create one first</option>}
                  {plants.map(p => (
                    <option key={p.plantId} value={p.plantId}>{p.name} ({p.plantId})</option>
                  ))}
                </select>
              </div>

              <div style={styles.formRow}>
                <label style={styles.label}>Role</label>
                <select
                  style={styles.select}
                  value={newInvite.role}
                  onChange={(e) =>
                    setNewInvite({ ...newInvite, role: e.target.value })
                  }
                >
                  <option value="operator">Operator</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div style={styles.formRow}>
                <label style={styles.label}>Expires In (Days)</label>
                <input
                  type="number"
                  style={styles.input}
                  value={newInvite.expiresInDays}
                  onChange={(e) =>
                    setNewInvite({
                      ...newInvite,
                      expiresInDays: parseInt(e.target.value)
                    })
                  }
                />
              </div>

              <div style={styles.formRow}>
                <label style={styles.label}>Pre-grant Features (optional)</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {['panel', 'scada', 'reports', 'sharing', 'alarms', 'templates'].map(f => {
                    const enabled = !!newInvite.features?.[f];
                    return (
                      <label key={f} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '5px 10px', borderRadius: 6,
                        background: enabled ? 'rgba(34,197,94,0.15)' : 'rgba(148,163,184,0.08)',
                        border: `1px solid ${enabled ? '#22c55e55' : '#334155'}`,
                        cursor: 'pointer', fontSize: 12,
                        color: enabled ? '#22c55e' : '#94a3b8'
                      }}>
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={(e) => setNewInvite({
                            ...newInvite,
                            features: { ...(newInvite.features || {}), [f]: e.target.checked }
                          })}
                          style={{ margin: 0 }}
                        />
                        {f}
                      </label>
                    );
                  })}
                </div>
              </div>

              <button style={styles.primaryBtn} onClick={generateInvitation}>
                <Ticket size={18} />
                Generate Code
              </button>
            </div>
          </div>

          {/* Invitations List */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h2 style={styles.cardTitle}>
                <Ticket size={20} />
                Active Invitations
              </h2>
              <button style={styles.iconBtn} onClick={fetchInvitations}>
                <RefreshCw size={18} />
              </button>
            </div>

            {loading ? (
              <div style={styles.loading}>Loading...</div>
            ) : (
              <div style={styles.table}>
                {invitations.map((inv) => (
                  <div
                    key={inv.id}
                    style={{
                      ...styles.row,
                      opacity: !inv.isValid ? 0.5 : 1
                    }}
                  >
                    <div style={styles.rowMain}>
                      <div style={styles.code}>
                        <code>{inv.codeMask || '****-****'}</code>
                      </div>
                      <div style={styles.badge}>
                        {inv.plantId} • {inv.role}
                      </div>
                    </div>

                    <div style={styles.rowDetails}>
                      <div style={styles.detail}>
                        <Clock size={14} />
                        {new Date(inv.expiresAt).toLocaleDateString()}
                      </div>
                      {inv.usedBy && (
                        <div style={styles.detail}>
                          <CheckCircle size={14} />
                          Used by {inv.usedBy}
                        </div>
                      )}
                      {!inv.isValid && (
                        <div style={{ ...styles.detail, color: '#ef4444' }}>
                          <XCircle size={14} />
                          Invalid
                        </div>
                      )}
                    </div>

                    {inv.isActive && (
                      <button
                        style={styles.dangerBtn}
                        onClick={() => revokeInvitation(inv.id)}
                      >
                        <Ban size={16} />
                        Revoke
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div style={styles.content}>
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h2 style={styles.cardTitle}>
                <Users size={20} />
                All Users
              </h2>
              <button style={styles.iconBtn} onClick={fetchUsers}>
                <RefreshCw size={18} />
              </button>
            </div>

            {loading ? (
              <div style={styles.loading}>Loading...</div>
            ) : (
              <div style={styles.table}>
                {users.map((user) => (
                  <div key={user.id} style={styles.row}>
                    <div style={styles.rowMain}>
                      <div>
                        <div style={styles.userName}>{user.name}</div>
                        <div style={styles.userEmail}>{user.email}</div>
                      </div>
                      <div style={styles.badge}>
                        {user.status === 'active' && (
                          <span style={{ color: '#22c55e' }}>Active</span>
                        )}
                        {user.status === 'pending' && (
                          <span style={{ color: '#eab308' }}>Pending</span>
                        )}
                        {user.status === 'suspended' && (
                          <span style={{ color: '#ef4444' }}>Suspended</span>
                        )}
                      </div>
                    </div>

                    <div style={styles.rowDetails}>
                      <div style={styles.detail}>
                        <Clock size={14} />
                        Joined {new Date(user.createdAt).toLocaleDateString()}
                      </div>
                    </div>

                    {/* Plant Access Management */}
                    <div style={styles.plantAccessSection}>
                      <div style={styles.plantAccessHeader}>
                        <span style={styles.plantAccessLabel}>
                          <Factory size={14} />
                          Plant Access
                        </span>
                      </div>
                      <div style={styles.plantAccessList}>
                        {user.plantAccess.map((pa, idx) => (
                          <div key={idx} style={styles.plantAccessTag}>
                            <Shield size={12} />
                            <span>{pa.plantId}</span>
                            <span style={styles.plantRoleBadge}>{pa.role}</span>
                            <button
                              style={styles.plantRemoveBtn}
                              onClick={() => removePlantAccess(user.id, pa.plantId)}
                              title={`Remove ${pa.plantId} access`}
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                        {/* Inline Add Plant Access */}
                        <div style={styles.plantAccessAddRow}>
                          <select
                            style={styles.plantAccessSelect}
                            id={`plant-select-${user.id}`}
                            defaultValue=""
                          >
                            <option value="" disabled>+ Add plant...</option>
                            {plants
                              .filter(p => !user.plantAccess.some(pa => pa.plantId === p.plantId))
                              .map(p => (
                                <option key={p.plantId} value={p.plantId}>{p.name} ({p.plantId})</option>
                              ))}
                          </select>
                          <select
                            style={styles.plantAccessSelect}
                            id={`role-select-${user.id}`}
                            defaultValue="operator"
                          >
                            <option value="operator">Operator</option>
                            <option value="manager">Manager</option>
                            <option value="admin">Admin</option>
                          </select>
                          <button
                            style={styles.plantAddBtn}
                            onClick={() => {
                              const plantSelect = document.getElementById(`plant-select-${user.id}`);
                              const roleSelect = document.getElementById(`role-select-${user.id}`);
                              if (plantSelect.value) {
                                addPlantAccess(user.id, plantSelect.value, roleSelect.value);
                                plantSelect.value = '';
                              }
                            }}
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Feature flags */}
                    <div style={styles.plantAccessSection}>
                      <div style={styles.plantAccessHeader}>
                        <span style={styles.plantAccessLabel}>
                          <Shield size={14} />
                          Features
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {['panel', 'scada', 'reports', 'sharing', 'alarms', 'templates'].map(f => {
                          const enabled = !!(user.features && user.features[f]);
                          return (
                            <label key={f} style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              padding: '5px 10px', borderRadius: 6,
                              background: enabled ? 'rgba(34,197,94,0.15)' : 'rgba(148,163,184,0.08)',
                              border: `1px solid ${enabled ? '#22c55e55' : '#334155'}`,
                              cursor: 'pointer', fontSize: 12,
                              color: enabled ? '#22c55e' : '#94a3b8'
                            }}>
                              <input
                                type="checkbox"
                                checked={enabled}
                                onChange={(e) => updateFeatures(user.id, {
                                  ...(user.features || {}),
                                  [f]: e.target.checked
                                })}
                                style={{ margin: 0 }}
                              />
                              {f}
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* Max sessions */}
                    <div style={styles.plantAccessSection}>
                      <div style={styles.plantAccessHeader}>
                        <span style={styles.plantAccessLabel}>
                          <Clock size={14} />
                          Max concurrent sessions
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input
                          type="number"
                          min="0"
                          placeholder="unlimited"
                          defaultValue={user.maxSessions ?? ''}
                          onBlur={(e) => {
                            const val = e.target.value === '' ? null : parseInt(e.target.value, 10);
                            if (val !== (user.maxSessions ?? null)) updateMaxSessions(user.id, val);
                          }}
                          style={{
                            width: 100, padding: '6px 10px', borderRadius: 6,
                            background: '#0f172a', border: '1px solid #334155',
                            color: '#e5e7eb', fontSize: 12
                          }}
                        />
                        <span style={{ fontSize: 11, color: '#64748b' }}>
                          {user.activeSessionCount ?? 0} active · empty = unlimited
                        </span>
                      </div>
                    </div>

                    <div style={styles.actions}>
                      {user.status === 'pending' && (
                        <button
                          style={styles.successBtn}
                          onClick={() => approveUser(user.id)}
                        >
                          <CheckCircle size={16} />
                          Approve
                        </button>
                      )}
                      {user.status === 'active' && (
                        <button
                          style={styles.dangerBtn}
                          onClick={() => suspendUser(user.id)}
                        >
                          <Ban size={16} />
                          Suspend
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {activeTab === 'plants' && (
        <div style={styles.content}>
          {/* Add New Plant */}
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>
              <Plus size={20} />
              Add New Plant
            </h2>
            <div style={styles.form}>
              <div style={styles.formRow}>
                <label style={styles.label}>Plant ID (unique key)</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="e.g. plantA"
                  value={newPlant.plantId}
                  onChange={(e) => setNewPlant({ ...newPlant, plantId: e.target.value.replace(/\s/g, '') })}
                />
              </div>
              <div style={styles.formRow}>
                <label style={styles.label}>Display Name</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="e.g. Factory Alpha"
                  value={newPlant.name}
                  onChange={(e) => setNewPlant({ ...newPlant, name: e.target.value })}
                />
              </div>
              <div style={styles.formRow}>
                <label style={styles.label}>Description (optional)</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="e.g. Main production facility"
                  value={newPlant.description}
                  onChange={(e) => setNewPlant({ ...newPlant, description: e.target.value })}
                />
              </div>
              <button style={styles.primaryBtn} onClick={createPlant}>
                <Factory size={18} />
                Create Plant
              </button>
            </div>
          </div>

          {/* Plants List */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h2 style={styles.cardTitle}>
                <Factory size={20} />
                Registered Plants
              </h2>
              <button style={styles.iconBtn} onClick={fetchPlants}>
                <RefreshCw size={18} />
              </button>
            </div>

            {loading ? (
              <div style={styles.loading}>Loading...</div>
            ) : plants.length === 0 ? (
              <div style={styles.loading}>No plants registered yet. Create one above.</div>
            ) : (
              <div style={styles.table}>
                {plants.map((plant) => (
                  <div key={plant.plantId} style={styles.row}>
                    <div style={styles.rowMain}>
                      <div>
                        <div style={styles.userName}>{plant.name}</div>
                        <div style={styles.userEmail}>{plant.plantId}</div>
                      </div>
                      <div style={styles.badge}>
                        {plant.description || 'No description'}
                      </div>
                    </div>
                    <div style={styles.rowDetails}>
                      <div style={styles.detail}>
                        <Clock size={14} />
                        Created {new Date(plant.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                    <div style={styles.actions}>
                      <button
                        style={styles.dangerBtn}
                        onClick={() => deletePlant(plant.plantId)}
                      >
                        <Trash2 size={16} />
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: '#0b0f19',
    color: '#e5e7eb',
    padding: '40px'
  },

  header: {
    marginBottom: 40
  },

  title: {
    fontSize: 36,
    fontWeight: 800,
    marginBottom: 24
  },

  tabs: {
    display: 'flex',
    gap: 8,
    borderBottom: '1px solid #1e293b'
  },

  tab: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 24px',
    background: 'none',
    border: 'none',
    color: '#9ca3af',
    fontSize: 16,
    cursor: 'pointer',
    borderBottom: '2px solid transparent',
    transition: 'all 0.2s'
  },

  tabActive: {
    color: '#a78bfa',
    borderBottomColor: '#a78bfa'
  },

  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24
  },

  card: {
    background: 'rgba(15,23,42,0.85)',
    backdropFilter: 'blur(20px)',
    borderRadius: 16,
    padding: 24,
    border: '1px solid #1e293b'
  },

  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20
  },

  cardTitle: {
    fontSize: 20,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    gap: 10
  },

  form: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 16,
    marginTop: 20
  },

  formRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },

  label: {
    fontSize: 14,
    color: '#9ca3af'
  },

  input: {
    padding: 12,
    background: '#020617',
    border: '1px solid #1e293b',
    borderRadius: 8,
    color: '#e5e7eb',
    fontSize: 15
  },

  select: {
    padding: 12,
    background: '#020617',
    border: '1px solid #1e293b',
    borderRadius: 8,
    color: '#e5e7eb',
    fontSize: 15
  },

  primaryBtn: {
    padding: '12px 24px',
    background: 'linear-gradient(90deg,#7c3aed,#22d3ee)',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    gridColumn: 'span 2'
  },

  table: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12
  },

  row: {
    padding: 16,
    background: '#020617',
    border: '1px solid #1e293b',
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 12
  },

  rowMain: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },

  code: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 16,
    fontFamily: 'monospace',
    color: '#22d3ee'
  },

  copyBtn: {
    padding: 4,
    background: 'none',
    border: 'none',
    color: '#9ca3af',
    cursor: 'pointer'
  },

  badge: {
    padding: '4px 12px',
    background: '#1e293b',
    borderRadius: 12,
    fontSize: 13,
    color: '#9ca3af'
  },

  rowDetails: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    fontSize: 13,
    color: '#6b7280'
  },

  detail: {
    display: 'flex',
    alignItems: 'center',
    gap: 6
  },

  userName: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 4
  },

  userEmail: {
    fontSize: 14,
    color: '#9ca3af'
  },

  actions: {
    display: 'flex',
    gap: 8,
    marginTop: 8
  },

  successBtn: {
    padding: '8px 16px',
    background: 'rgba(34, 197, 94, 0.1)',
    border: '1px solid rgba(34, 197, 94, 0.3)',
    borderRadius: 6,
    color: '#22c55e',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6
  },

  dangerBtn: {
    padding: '8px 16px',
    background: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: 6,
    color: '#ef4444',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6
  },

  iconBtn: {
    padding: 8,
    background: 'none',
    border: 'none',
    color: '#9ca3af',
    cursor: 'pointer'
  },

  loading: {
    padding: 40,
    textAlign: 'center',
    color: '#9ca3af'
  },

  plantAccessSection: {
    borderTop: '1px solid #1e293b',
    paddingTop: 12
  },

  plantAccessHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8
  },

  plantAccessLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    color: '#9ca3af',
    fontWeight: 600
  },

  plantAccessList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center'
  },

  plantAccessTag: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 8px',
    background: 'rgba(167, 139, 250, 0.1)',
    border: '1px solid rgba(167, 139, 250, 0.25)',
    borderRadius: 6,
    fontSize: 13,
    color: '#a78bfa'
  },

  plantRoleBadge: {
    fontSize: 11,
    padding: '1px 6px',
    background: 'rgba(34, 211, 238, 0.15)',
    color: '#22d3ee',
    borderRadius: 4
  },

  plantRemoveBtn: {
    padding: 2,
    background: 'none',
    border: 'none',
    color: '#ef4444',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    opacity: 0.6,
    transition: 'opacity 0.2s'
  },

  plantAccessAddRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4
  },

  plantAccessSelect: {
    padding: '4px 8px',
    background: '#020617',
    border: '1px solid #1e293b',
    borderRadius: 6,
    color: '#9ca3af',
    fontSize: 12
  },

  plantAddBtn: {
    padding: '4px 8px',
    background: 'rgba(34, 197, 94, 0.1)',
    border: '1px solid rgba(34, 197, 94, 0.3)',
    borderRadius: 6,
    color: '#22c55e',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center'
  }
};